import React, { FC, useState } from 'react';
import { Plus } from '@menuzen/icon/build/outline';
import { Button, Box, useTheme, Switch } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DehazeIcon from '@mui/icons-material/Dehaze';

import ChevronRightIcon from '../../common/icon/ChevronDownRight';
import { BuilderComponentProps, useNode } from '@mnz/builder';
import LabelIcon from '../../common/components/label-icon/LabelIcon';
import { MAX_ACCOUNT_SOCIAL_LINK } from '../../constant/account';
import { socialOptions } from '../../constant/socialLinks';
import { capitalizeFirstLetter } from '../../shared/utils/string';
import EditSocialLinkModal, { getUsernameFromUrl } from './EditSocialLinkModal';
import { px2rem } from '../../core';
import { boxCss, descriptionCss } from '../../common/style';
import { _order, reorder } from '../../shared/utils';
import { useDragAndDrop } from '../../shared/hooks/useDragAndDrop';

enum SocialValueType {
    LINK = 'link',
    USERNAME = 'username',
    PHONE = 'phone',
}

export interface SocialOptionData {
    value: string;
    title: string;
    placeholder?: string;
    outlineIcon?: React.ReactElement;
    shapeIcon?: React.ReactElement;
    solidIcon?: React.ReactElement;
    valueType: SocialValueType;
    prefix?: string[]; // for auto correct
    handleLink?: (value: string) => string;
}

const renderSocialTitle = (social: string) => {
    const socialLink: SocialOptionData | undefined = socialOptions.find((socialLink) => {
        return socialLink.value == social;
    });
    if (socialLink && socialLink.title) {
        return socialLink.title;
    }
    return capitalizeFirstLetter(social);
};

export interface LinksSettingProps extends BuilderComponentProps {
    hideSocialLinkItems: Record<number, boolean>;
    order?: number[];
}

export const SocialSetting: FC<LinksSettingProps> = () => {
    const theme = useTheme();
    const {
        props: { account, hideSocialLinkItems, order: defaultOrder },
        actions: { setProp },
    } = useNode((node) => ({
        hidden: node.data.hidden,
        props: node.data.props as unknown as LinksSettingProps,
    }));
    const [editItems, setEditItem] = useState(null);
    const [order, setOrder] = useState(defaultOrder || []);
    const socialAccounts = _order(account?.socialLinks, order || []);

    const [modal, setModal] = useState<'EDIT' | 'LIST'>('LIST');
    const background = 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)';

    const { dragCss, placeholderCss, onDragEnd: handleOnDragEnd, onDragUpdate } = useDragAndDrop();

    const onDragEnd = (res: DropResult) => {
        if (!res.destination) {
            return;
        }
        handleOnDragEnd();
        const newOrderLink: any[] = reorder(socialAccounts, res.source.index, res.destination.index);
        const newOrder = newOrderLink.map((order: any) => order.id);
        setProp<LinksSettingProps>((props) => {
            props.order = newOrder;
        });
        setOrder(newOrder);
    };

    return (
        <Box sx={{ my: '20px' }} display="flex" flexDirection="column">
            <EditSocialLinkModal
                selectedSocialLink={editItems}
                setSelectedSocialLink={setEditItem}
                account={account}
                onClose={() => setModal('LIST')}
                open={modal == 'EDIT'}
            />
            <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                <Droppable droppableId="orderLink-droppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            css={{ overflow: 'auto', maxHeight: '450px', position: 'relative' }}
                        >
                            {socialAccounts &&
                                socialAccounts.map((socialLink, index) => (
                                    <Draggable key={socialLink.id} draggableId={`${socialLink.id}`} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                css={{ marginBottom: 10 }}
                                                onClick={() => {
                                                    setEditItem(socialLink);
                                                    setModal('EDIT');
                                                }}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <div
                                                    css={[
                                                        snapshot.isDragging ? dragCss : {},
                                                        boxCss(theme),
                                                        {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '10px 16px',
                                                            backgroundColor: 'white',
                                                            overflow: 'hidden',
                                                        },
                                                    ]}
                                                >
                                                    <div className="text-16 mr-8">
                                                        <DehazeIcon
                                                            sx={{
                                                                color: (theme) => theme.palette.primary50,
                                                                fontSize: '16px',
                                                            }}
                                                        />
                                                    </div>
                                                    <Box
                                                        sx={{
                                                            marginRight: '8px',
                                                            height: '40px',
                                                            width: '40px',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            borderRadius: '5px',
                                                            alignItems: 'center',
                                                            background,
                                                        }}
                                                    >
                                                        <LabelIcon
                                                            type={'outline'}
                                                            icon={capitalizeFirstLetter(socialLink.type)}
                                                            size={24}
                                                        />
                                                    </Box>
                                                    <div css={{ flex: '1 1 auto', textAlign: 'left' }}>
                                                        <div css={{ lineHeight: px2rem(16) }}>
                                                            {renderSocialTitle(socialLink.type)}
                                                        </div>
                                                        <div
                                                            css={[
                                                                descriptionCss(theme),
                                                                { lineHeight: px2rem(12), fontSize: px2rem(10) },
                                                            ]}
                                                        >
                                                            {getUsernameFromUrl(socialLink.link, socialLink.type)}
                                                        </div>
                                                    </div>
                                                    <div css={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                                                        <Switch
                                                            checked={
                                                                hideSocialLinkItems &&
                                                                typeof hideSocialLinkItems[socialLink.id] !==
                                                                    'undefined'
                                                                    ? !hideSocialLinkItems[socialLink.id]
                                                                    : true
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                setProp<LinksSettingProps>((props) => {
                                                                    if (typeof hideSocialLinkItems === 'undefined') {
                                                                        props.hideSocialLinkItems = {};
                                                                    }
                                                                    if (
                                                                        typeof hideSocialLinkItems[socialLink.id] ===
                                                                        'undefined'
                                                                    ) {
                                                                        props.hideSocialLinkItems[socialLink.id] = true;
                                                                    }
                                                                    props.hideSocialLinkItems[socialLink.id] =
                                                                        !e.target.checked;
                                                                });
                                                            }}
                                                        />
                                                        <div
                                                            css={{
                                                                color: theme.palette.primary50,
                                                                fontSize: px2rem(20),
                                                                paddingLeft: px2rem(10),
                                                            }}
                                                        >
                                                            <ChevronRightIcon />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                            {provided.placeholder}
                            <Box
                                sx={{
                                    ...placeholderCss,
                                    borderRadius: '8px',
                                }}
                            />
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {(!socialAccounts || socialAccounts.length < MAX_ACCOUNT_SOCIAL_LINK) && (
                <div css={{ paddingTop: px2rem(6) }}>
                    <Button
                        variant="dashed"
                        color="success"
                        fullWidth
                        onClick={() => {
                            setEditItem(null);
                            setModal('EDIT');
                        }}
                        style={{ borderRadius: px2rem(8), borderWidth: 1, padding: '12px' }}
                    >
                        <Plus />
                        <span css={{ paddingLeft: px2rem(10) }}>New Social Link</span>
                    </Button>
                </div>
            )}
        </Box>
    );
};
